/**
 * Offline Indicator Component
 * 
 * Displays offline status, sync status, and pending actions count.
 */

import React, { useState, useEffect } from 'react';
import { View, Pressable, Animated } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useTheme } from '@/theme';
import { ThemedText, Caption } from './Text';
import { Card } from './Card';
import { networkMonitor } from '@/services/networkMonitor';
import { syncQueue } from '@/services/syncQueue';
import { getLastSyncTime } from '@/services/offlineTrips';
import { getSyncStatus, subscribeToSyncStatus, type SyncStatus } from '@/services/offlineSync';
import { formatDistanceToNow } from 'date-fns';
import { SyncStatusModal } from '@/components/offline/SyncStatusModal';
import { ConflictResolutionModal } from '@/components/offline/ConflictResolutionModal';

const OFFLINE_BANNER_DELAY_MS = 1500;

type OfflineIndicatorProps = {
  variant?: 'banner' | 'compact' | 'badge';
  showPendingCount?: boolean;
  showLastSync?: boolean;
  onPress?: () => void;
  enableSyncModal?: boolean; // If true, opens SyncStatusModal on press
  enableConflictModal?: boolean; // If true, opens ConflictResolutionModal on press
};

export function OfflineIndicator({
  variant = 'banner',
  showPendingCount = true,
  showLastSync = true,
  onPress,
  enableSyncModal = false,
  enableConflictModal = false,
}: OfflineIndicatorProps) {
  const theme = useTheme();
  const [rawIsOnline, setRawIsOnline] = useState(networkMonitor.isOnline());
  const [isOnline, setIsOnline] = useState(networkMonitor.isOnline());
  const [pendingCount, setPendingCount] = useState(0);
  const [failedCount, setFailedCount] = useState(0);
  const [lastSyncTime, setLastSyncTime] = useState<number | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [conflicts, setConflicts] = useState(0);
  const [showSyncModal, setShowSyncModal] = useState(false);
  const [showConflictModal, setShowConflictModal] = useState(false);
  const pulseAnim = useState(new Animated.Value(1))[0];

  useEffect(() => {
    if (rawIsOnline) {
      setIsOnline(true);
      return;
    }

    // App resume can briefly emit disconnected/unreachable before probes settle.
    // Delay the visible offline state so users don't see false offline flashes.
    const timer = setTimeout(() => {
      setIsOnline(false);
    }, OFFLINE_BANNER_DELAY_MS);

    return () => clearTimeout(timer);
  }, [rawIsOnline]);

  useEffect(() => {
    // Keep UI aligned with networkMonitor (includes unknown-state + isInternetReachable rules).
    void networkMonitor.refreshStatus().then(() => {
      setRawIsOnline(networkMonitor.isOnline());
    }).catch(() => {
      // Keep existing state if refresh fails transiently.
    });

    const unsubscribeNetwork = networkMonitor.addListener(() => {
      setRawIsOnline(networkMonitor.isOnline());
    });

    // Subscribe to sync status for conflicts
    const unsubscribeSync = subscribeToSyncStatus((status: SyncStatus) => {
      setPendingCount(status.pendingChanges);
      setFailedCount(status.failedChanges);
      setLastSyncTime(status.lastSync.getTime());
      setIsProcessing(status.isSyncing);
      setConflicts(status.conflicts);
    });

    return () => {
      unsubscribeNetwork();
      unsubscribeSync();
    };
  }, []);

  useEffect(() => {
    // Update last sync time
    const updateLastSync = async () => {
      const time = await getLastSyncTime();
      setLastSyncTime(time);
    };

    updateLastSync();
    const interval = setInterval(updateLastSync, 30000); // Update every 30 seconds

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    // Subscribe to processing status
    const unsubscribe = syncQueue.onProcessingChange((processing) => {
      setIsProcessing(processing);
    });

    return unsubscribe;
  }, []);

  useEffect(() => {
    // Subtle pulse on the status icon only when offline (calmer than pulsing the whole card).
    if (!isOnline) {
      const loop = Animated.loop(
        Animated.sequence([
          Animated.timing(pulseAnim, {
            toValue: 0.82,
            duration: 1400,
            useNativeDriver: true,
          }),
          Animated.timing(pulseAnim, {
            toValue: 1,
            duration: 1400,
            useNativeDriver: true,
          }),
        ]),
      );
      loop.start();
      return () => loop.stop();
    }
    pulseAnim.setValue(1);
    return undefined;
  }, [isOnline, pulseAnim]);

  // Don't show anything if online and no pending actions, failures, or conflicts
  if (isOnline && pendingCount === 0 && failedCount === 0 && conflicts === 0 && variant !== 'badge') {
    return null;
  }

  const formatLastSync = (timestamp: number | null): string => {
    if (!timestamp) return 'Never';
    try {
      return formatDistanceToNow(new Date(timestamp), { addSuffix: true });
    } catch {
      return 'Unknown';
    }
  };

  const statusMetaItems: React.ReactNode[] = [];
  const hasSyncFailures = failedCount > 0 || conflicts > 0;
  /** Offline or merge conflicts — keep strong (red) treatment. */
  const useCriticalBanner = !isOnline || conflicts > 0;
  /** Online with only dead-letter failures — calmer warning (not the same as offline). */
  const useWarningBanner = isOnline && failedCount > 0 && conflicts === 0;

  const primaryMutedColor = useWarningBanner
    ? theme.colors.onWarningContainer
    : useCriticalBanner || hasSyncFailures
      ? theme.colors.onErrorContainer
      : theme.colors.onSuccessContainer;

  const statusTextColor =
    isOnline && !hasSyncFailures ? theme.colors.onSuccessContainer : primaryMutedColor;

  if (showPendingCount && pendingCount > 0) {
    statusMetaItems.push(
      <Caption key="pending" color={statusTextColor}>
        {pendingCount} action{pendingCount !== 1 ? 's' : ''} pending sync
      </Caption>
    );
  }

  if (conflicts > 0) {
    statusMetaItems.push(
      <Caption key="conflicts" color={theme.colors.onErrorContainer}>
        {conflicts} conflict{conflicts !== 1 ? 's' : ''} need resolution
      </Caption>
    );
  }

  if (failedCount > 0) {
    statusMetaItems.push(
      <Caption key="failed" color={useWarningBanner ? theme.colors.onWarningContainer : theme.colors.onErrorContainer}>
        {failedCount === 1
          ? "1 change didn't finish uploading — tap for details"
          : `${failedCount} changes didn't finish uploading — tap for details`}
      </Caption>
    );
  }

  if (showLastSync && lastSyncTime) {
    statusMetaItems.push(
      <Caption key="lastSync" color={statusTextColor}>
        Last synced {formatLastSync(lastSyncTime)}
      </Caption>
    );
  }

  if (!isOnline) {
    statusMetaItems.push(
      <Caption key="offlineNote" color={theme.colors.onErrorContainer}>
        Changes will sync when you're back online
      </Caption>
    );
  }

  const handlePress = () => {
    if (onPress) {
      onPress();
    } else if (enableConflictModal && conflicts > 0) {
      setShowConflictModal(true);
    } else if (enableSyncModal) {
      setShowSyncModal(true);
    }
  };
  const canOpenDetails = Boolean(onPress || enableSyncModal || (enableConflictModal && conflicts > 0));

  if (variant === 'badge') {
    const badgeFailureBg = useCriticalBanner ? theme.colors.error : theme.colors.warning;
    const badgeFailureFg = useCriticalBanner ? theme.colors.onError : theme.colors.onWarning;
    // Small badge for header
    return (
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          gap: theme.spacing(0.5),
        }}
      >
        {!isOnline && (
          <Ionicons
            name="cloud-offline"
            size={16}
            color={theme.colors.error}
          />
        )}
        {(pendingCount > 0 || failedCount > 0 || conflicts > 0) && (
          <View
            style={{
              backgroundColor: hasSyncFailures ? badgeFailureBg : theme.colors.warning,
              borderRadius: theme.radii.pill,
              minWidth: 18,
              height: 18,
              alignItems: 'center',
              justifyContent: 'center',
              paddingHorizontal: theme.spacing(0.5),
            }}
          >
            <ThemedText
              variant="caption"
              style={{
                color: hasSyncFailures ? badgeFailureFg : theme.colors.onWarning,
                fontSize: 10,
                fontWeight: '600',
              }}
            >
              {pendingCount + failedCount + conflicts > 99 ? '99+' : pendingCount + failedCount + conflicts}
            </ThemedText>
          </View>
        )}
      </View>
    );
  }

  if (variant === 'compact') {
    const compactBg = !isOnline
      ? theme.colors.errorContainer
      : useCriticalBanner
        ? theme.colors.errorContainer
        : useWarningBanner
          ? theme.colors.warningContainer
          : theme.colors.successContainer;
    const compactFg = !isOnline
      ? theme.colors.onErrorContainer
      : useCriticalBanner
        ? theme.colors.onErrorContainer
        : useWarningBanner
          ? theme.colors.onWarningContainer
          : theme.colors.onSuccessContainer;
    // Compact inline indicator
    return (
      <>
        <Pressable onPress={handlePress} disabled={!canOpenDetails}>
        <View
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            gap: theme.spacing(1),
            paddingVertical: theme.spacing(1),
            paddingHorizontal: theme.spacing(1.5),
            backgroundColor: compactBg,
            borderRadius: theme.radii.md,
          }}
        >
          <Ionicons
            name={isOnline ? 'cloud-done' : 'cloud-offline'}
            size={16}
            color={compactFg}
          />
          <ThemedText variant="caption" color={compactFg}>
            {useWarningBanner ? 'Sync' : isOnline ? 'Online' : 'Offline'}
          </ThemedText>
          {pendingCount > 0 && (
            <ThemedText variant="caption" color={compactFg}>
              • {pendingCount} pending
            </ThemedText>
          )}
          {failedCount > 0 && (
            <ThemedText
              variant="caption"
              color={useWarningBanner ? theme.colors.onWarningContainer : theme.colors.onErrorContainer}
            >
              • {failedCount} stuck
            </ThemedText>
          )}
        </View>
      </Pressable>
      {showSyncModal && (
        <SyncStatusModal
          visible={showSyncModal}
          onClose={() => setShowSyncModal(false)}
        />
      )}
      {showConflictModal && (
        <ConflictResolutionModal
          visible={showConflictModal}
          onClose={() => setShowConflictModal(false)}
          onResolved={() => {
            // Refresh conflicts count
            getSyncStatus().then(status => setConflicts(status.conflicts));
          }}
        />
      )}
      </>
    );
  }

  // Banner variant (default): warning (amber) when online + dead-letter only; red for offline or conflicts.
  const bannerBg = !isOnline
    ? theme.colors.errorContainer
    : useCriticalBanner
      ? theme.colors.errorContainer
      : useWarningBanner
        ? theme.colors.warningContainer
        : theme.colors.successContainer;
  const bannerBorder = !isOnline
    ? theme.colors.error + '45'
    : useCriticalBanner
      ? theme.colors.error + '40'
      : useWarningBanner
        ? theme.colors.warning + '55'
        : theme.colors.primary + '35';

  const bannerIconTint = useWarningBanner
    ? theme.colors.warning
    : isOnline && !hasSyncFailures
      ? theme.colors.primary
      : theme.colors.error;
  const bannerIconColor = useWarningBanner
    ? theme.colors.onWarningContainer
    : isOnline && !hasSyncFailures
      ? theme.colors.onSuccessContainer
      : theme.colors.onErrorContainer;

  return (
    <>
      <Card
        style={{
          backgroundColor: bannerBg,
          marginBottom: theme.spacing(1),
          borderWidth: 1,
          borderColor: bannerBorder,
          paddingVertical: theme.spacing(1),
          paddingHorizontal: theme.spacing(1.25),
        }}
      >
        <Pressable onPress={handlePress} disabled={!canOpenDetails}>
          <View
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              gap: theme.spacing(1),
            }}
          >
            <Animated.View style={{ opacity: !isOnline ? pulseAnim : 1 }}>
              <View
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: theme.radii.md,
                  backgroundColor: `${bannerIconTint}22`,
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Ionicons
                  name={useWarningBanner ? 'cloud-upload-outline' : isOnline ? 'cloud-done' : 'cloud-offline'}
                  size={18}
                  color={bannerIconColor}
                />
              </View>
            </Animated.View>
            <View style={{ flex: 1, gap: 2 }}>
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(1), flexWrap: 'wrap' }}>
                <ThemedText variant="bodyStrong" color={bannerIconColor}>
                  {useWarningBanner ? 'Sync issue' : isOnline ? 'You\'re online' : 'You\'re offline'}
                </ThemedText>
              </View>
              <View style={{ flexDirection: 'row', gap: theme.spacing(1), flexWrap: 'wrap' }}>
                {statusMetaItems.map((item, index) => (
                  <React.Fragment key={`meta-${index}`}>
                    {index > 0 && <Caption color={statusTextColor}>{' • '}</Caption>}
                    {item}
                  </React.Fragment>
                ))}
              </View>
            </View>
            {(onPress || enableSyncModal || enableConflictModal) && (
              <Ionicons
                name="chevron-forward"
                size={20}
                color={bannerIconColor}
              />
            )}
          </View>
        </Pressable>
      </Card>
    {showSyncModal && (
      <SyncStatusModal
        visible={showSyncModal}
        onClose={() => setShowSyncModal(false)}
      />
    )}
    {showConflictModal && (
      <ConflictResolutionModal
        visible={showConflictModal}
        onClose={() => setShowConflictModal(false)}
        onResolved={() => {
          // Refresh conflicts count
          getSyncStatus().then(status => setConflicts(status.conflicts));
        }}
      />
    )}
    </>
  );
}

